import { useCallback } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { getGetMarketingBulkSmsQueryKey, useGetMarketingBulkSms } from '@/api/generated/marketing/marketing';
import type { GetMarketingBulkSms200 } from '@/api/generated/schemas';
import { isActiveSendStatus } from '@/features/marketing/bulk-sms/bulk-sms-format';
import type { BulkSmsHistoryItem } from '@/features/marketing/bulk-sms/bulk-sms-types';
import { marketingQueryKeys } from '@/features/marketing/use-marketing-queries';
import { useTextClientsFocusViewSearch } from './use-text-clients-focus-view-search';

/** How often the pill re-reads its numbers while a send is going out. Never while idle or the tab is hidden. */
export const ACTIVE_SEND_POLL_MS = 15_000;
/** Paused, or waiting for a later wave: hours away, but a Resume from another device still shows up within a
 *  minute (the detail screen's own cadence for the same states). */
export const WAITING_SEND_POLL_MS = 60_000;

/** The salon's one running send. History is most recently sent first, so the active batch is on its first page. */
function findActiveSend(history: GetMarketingBulkSms200): BulkSmsHistoryItem | null {
  return history.data.find((item) => isActiveSendStatus(item.status)) ?? null;
}

/** The pill's refresh cadence for this history: fast while texts go out, slow while paused / waiting, none idle. */
export function activeSendPollInterval(history: GetMarketingBulkSms200 | undefined): number | false {
  const send = history ? findActiveSend(history) : null;
  if (!send) return false;
  return send.status === 'PAUSED' || send.heldUntil ? WAITING_SEND_POLL_MS : ACTIVE_SEND_POLL_MS;
}

/**
 * True for the bulk-send history reads (Recent sends, and the Texts & Emails pages under the same prefix). The Text
 * focus view's marketing session spares them: the pill on the Clients page under the view keeps observing the list
 * while the view is open.
 */
export function isBulkSendsHistoryQueryKey(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === getGetMarketingBulkSmsQueryKey()[0];
}

/**
 * The salon's running bulk send (queued, sending, waiting between waves or paused), or null. It reads the Recent
 * sends history under the same cache key, so a Send / Pause / Resume / Cancel / finish (which invalidate that key)
 * shows or hides the pill at once. It polls only while a send is active (see {@link activeSendPollInterval}) and
 * the tab is visible.
 *
 * `orgRouteParam` is the URL's `$orgId` segment (the salon short code), which the bulk-SMS keys use.
 * `enabled`: the viewer may read bulk sends (`useMarketingAccess().canReadBulk`).
 */
export function useActiveBulkSend(orgRouteParam: string, enabled: boolean): BulkSmsHistoryItem | null {
  const { data } = useGetMarketingBulkSms(undefined, {
    query: {
      queryKey: marketingQueryKeys.bulkSends(orgRouteParam),
      enabled,
      select: findActiveSend,
      refetchInterval: (query) => activeSendPollInterval(query.state.data),
    },
  });
  // A disabled query can still hold data cached before access changed: never show it.
  return enabled ? (data ?? null) : null;
}

interface ViewBulkSendOptions {
  /** Someone is ticked: leaving Clients would drop the picks, so a Leads send opens in a new tab instead. */
  keepPage?: boolean;
}

/**
 * Open a running send where it belongs: a Clients send in the focus view over this page, a Leads send on its
 * Marketing page (its rail, its context).
 */
export function useViewBulkSend(orgRouteParam: string) {
  const navigate = useNavigate();
  const router = useRouter();
  const { open } = useTextClientsFocusViewSearch(orgRouteParam);
  return useCallback(
    (send: Pick<BulkSmsHistoryItem, 'id' | 'audience'>, { keepPage = false }: ViewBulkSendOptions = {}) => {
      if (send.audience === 'CLIENTS') {
        void open(send.id);
        return;
      }
      const target = {
        to: '/$orgId/marketing/bulk-sms/$bulkSendId',
        params: { orgId: orgRouteParam, bulkSendId: send.id },
      } as const;
      if (keepPage) {
        window.open(router.buildLocation(target).href, '_blank', 'noopener');
        return;
      }
      void navigate(target);
    },
    [navigate, open, orgRouteParam, router]
  );
}
